// The adoption ribbon: one row per school, weeks along x, lesson volume as
// colour density, detected focus cycles outlined.
//
// It answers a question nothing else on the dashboard answers — *who pushes, and
// when* — and it is the one view where staggered adoption is visible as a fact
// rather than as an absence. A district whose schools each run a real push at
// different times has NO district-level cycle (prod: Blue Springs scores 1.47x
// against a 1.5x gate while 14 of its 16 schools each have one), so every
// district-scoped read of that district reports "usage is evenly spread", which
// is true of the aggregate and false of every school in it. The ribbon shows the
// stagger directly. It also doubles as a PD-scheduling artifact: the empty
// columns are when nobody is teaching with ABii.
//
// Visual language is deliberately shared with the worklist's pattern micro-bar
// and the sparkline cycle band: same density encoding, same band fill. Whatever
// the ribbon shows for the district, those show for one row — so a reader who
// learns to read one has learned to read all three.
//
// Scaling is the one real design decision here. Each row is scaled to the
// DISTRICT's peak week, not its own, which is the opposite of the pattern
// micro-bar's choice and for a reason: the micro-bar sits in a row already
// carrying roster and reach, and answers "when did THIS school work"; the ribbon
// is a cross-school comparison, and per-row scaling would draw a school running
// 40 lessons a week exactly like one running 4,000. The cost is that small
// schools read faint, which is why the row label carries its own total and the
// hover carries the week's count.
import React, { useEffect, useMemo, useState } from "react";
import FA from "../../fa";
import {
  INK,
  MUTED,
  GRID,
  SURFACE,
  CARD_BORDER,
  CARD_RADIUS,
} from "../tokens";
import { fmtBucketLabel } from "../format";
import { CYCLE_BAND_FILL, CYCLE_BAND_STROKE } from "../primitives";
import { SectionHead, PanelHead, PlotNote } from "../ui";

// Density ramp. A week with any lessons at all must be visibly darker than an
// empty one — the floor is what makes "ran a little" distinguishable from "ran
// nothing", which is the ribbon's primary read.
const CELL_MIN_ALPHA = 0.14;
const ROW_H = 15;
const ROW_GAP = 3;
const LABEL_W = 190;
const TOTAL_W = 62;
const CARD_PAD_X = 14;

// Where the ribbon's *time* axis begins and ends, measured from the outside of
// its card (1px border + padding + the fixed label / total columns + the row's
// 8px flex gaps).
//
// Exported because the engagement mix is stacked directly beneath the ribbon and
// plots the same weeks: pinning the mix's chart area to these offsets makes the
// two share one timeline, so a dark column in the ribbon sits directly above the
// bar it produced. Without it the two plots agree about the data and disagree
// about where March is, which is what keeps the pair from reading as a pair.
const RIBBON_GUTTER_LEFT = 1 + CARD_PAD_X + LABEL_W + 8;
const RIBBON_GUTTER_RIGHT = 1 + CARD_PAD_X + TOTAL_W + 8;

// Cycle outline: a stroked box around the weeks a push covers, over the density
// so it reads as an annotation on top rather than another band of colour. Shared
// with the mix strip below (see primitives.CYCLE_BAND_STROKE).
const CYCLE_STROKE = CYCLE_BAND_STROKE;

const RibbonRow = ({
  row,
  max,
  buckets,
  interval,
  cycles,
  onHover,
}) => {
  const vol = row.volume_by_bucket || [];
  const total = vol.reduce((a, b) => a + b, 0);
  // Index 0 is the window-start instant the server pads to zero (see
  // vanroboapp/segments/volume.py), so it is never a real week. Dropping it here
  // keeps the strip from opening with a permanently empty column.
  const cells = vol.slice(1);
  const stamps = buckets.slice(1);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div
        title={row.name}
        style={{
          width: LABEL_W,
          flex: `0 0 ${LABEL_W}px`,
          fontSize: 11.5,
          color: INK,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {row.name}
      </div>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          gap: 1,
          height: ROW_H,
          position: "relative",
        }}
      >
        {cells.map((v, i) => {
          const inCycle = cycles.some(([lo, hi]) => i + 1 >= lo && i + 1 <= hi);
          const alpha = max && v ? CELL_MIN_ALPHA + (1 - CELL_MIN_ALPHA) * (v / max) : 0;
          return (
            <div
              key={i}
              onMouseEnter={
                onHover
                  ? () =>
                      onHover({
                        name: row.name,
                        label: stamps[i]
                          ? fmtBucketLabel(stamps[i], interval)
                          : "",
                        lessons: v,
                      })
                  : undefined
              }
              title={`${row.name} — ${
                stamps[i] ? fmtBucketLabel(stamps[i], interval) : ""
              }: ${v.toLocaleString()} lessons`}
              style={{
                flex: 1,
                minWidth: 0,
                // the in-cycle tint sits UNDER the density, so a busy week inside
                // a push still reads as busy rather than as amber
                background: inCycle ? CYCLE_BAND_FILL : "transparent",
                boxShadow: v
                  ? `inset 0 0 0 ${ROW_H}px rgba(43, 42, 40, ${alpha.toFixed(3)})`
                  : undefined,
                borderRadius: 1,
              }}
            />
          );
        })}
        {/* cycle outlines, drawn as an overlay so they can span cells */}
        {cycles.map(([lo, hi], i) => {
          const n = cells.length || 1;
          const left = ((lo - 1) / n) * 100;
          const width = ((hi - lo + 1) / n) * 100;
          return (
            <div
              key={`c${i}`}
              style={{
                position: "absolute",
                top: -1,
                bottom: -1,
                left: `${left}%`,
                width: `${width}%`,
                border: `1px solid ${CYCLE_STROKE}`,
                borderRadius: 2,
                pointerEvents: "none",
              }}
            />
          );
        })}
      </div>
      <div
        style={{
          width: TOTAL_W,
          flex: `0 0 ${TOTAL_W}px`,
          textAlign: "right",
          fontSize: 11,
          color: MUTED,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {total.toLocaleString()}
      </div>
    </div>
  );
};

// Sparse x-axis: a label every Nth bucket, because 52 weekly labels do not fit
// and a strip with none is unreadable as a timeline.
const RibbonAxis = ({ buckets, interval, every, style }) => (
  <div
    style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, ...style }}
  >
    <div style={{ width: LABEL_W, flex: `0 0 ${LABEL_W}px` }} />
    <div style={{ flex: 1, minWidth: 0, display: "flex", gap: 1 }}>
      {buckets.slice(1).map((b, i) => (
        <div
          key={i}
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 9.5,
            color: MUTED,
            whiteSpace: "nowrap",
            overflow: "visible",
          }}
        >
          {i % every === 0 ? fmtBucketLabel(b, interval) : ""}
        </div>
      ))}
    </div>
    <div style={{ width: TOTAL_W, flex: `0 0 ${TOTAL_W}px` }} />
  </div>
);

// Cycle bucket ranges for one segment, from the server's per-segment detection.
// Indices are into the padded volume array (so 1-based against the strip), which
// is why this can't reuse cycleBandsForBuckets — that one reads explicit bucket
// spans, and here the spans are the boundary list.
const cycleRanges = (detected, buckets) => {
  if (!detected || !detected.cycles || !detected.cycles.length) return [];
  const ms = (v) => new Date(v).getTime();
  const out = [];
  detected.cycles.forEach((c) => {
    const cs = ms(c.start);
    const ce = ms(c.end);
    let lo = -1;
    let hi = -1;
    for (let i = 1; i < buckets.length; i++) {
      if (ms(buckets[i]) > cs && ms(buckets[i - 1]) < ce) {
        if (lo < 0) lo = i;
        hi = i;
      }
    }
    if (lo >= 0) out.push([lo, hi]);
  });
  return out;
};

// `bare` — the ribbon is the lower panel of the merged "How the year was used"
// card, so it draws only its caption, rows, axis and footer, with no card of its
// own: the caller owns the surface both panels share, and the caller's mix chart
// is pinned to the same gutters. The fetch stays here either way, because it is
// the slowest call on the page and nothing else should wait on it.
const AdoptionRibbon = ({
  api,
  org,
  windowSpec,
  dimension = "school",
  bare,
  showAxis = true,
}) => {
  const [state, setState] = useState({ loading: true });
  const [hover, setHover] = useState(null);
  const orgId = org && org.id;
  const { start, end } = windowSpec;

  // Fetched by this component, on its own, deliberately. The per-school cycle
  // fan-out is the slowest thing on the page, and the mix strip and dose-response
  // below must not wait on it — so the ribbon renders its own spinner in its own
  // slot and fills in when it lands.
  useEffect(() => {
    if (!orgId) {
      setState({ loading: false });
      return undefined;
    }
    let cancelled = false;
    setState({ loading: true });
    api
      .fetchSegmentLessonVolume({
        dimension,
        id: orgId,
        start,
        end,
        // Weekly whatever the page's grain: a cycle is 3–12 weeks, and at monthly
        // grain a push is two or three cells with no shape to outline.
        interval: "week",
        include: "cycles",
      })
      .then((d) => {
        if (!cancelled) setState({ loading: false, data: d });
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, failed: true });
      });
    return () => {
      cancelled = true;
    };
    // `api` is recreated every render by withAPI — depending on it never settles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, dimension, start, end]);

  const model = useMemo(() => {
    const d = state.data;
    if (!d || !d.segments) return null;
    // Rows with no lessons at all are dropped rather than drawn as empty strips.
    // A school that never started is a finding, but it belongs in the Outliers
    // worklist ("Not started") where it can be ranked and acted on — here it
    // would be a blank line pushing the schools that DID run pushes off screen.
    const rows = d.segments
      .filter((s) => (s.volume_by_bucket || []).some((v) => v))
      .map((s) => ({
        ...s,
        total: (s.volume_by_bucket || []).reduce((a, b) => a + b, 0),
        cycleRanges: cycleRanges(
          d.cycles && d.cycles[s.id],
          d.timestamps || []
        ),
      }))
      .sort((a, b) => b.total - a.total);
    const max = rows.reduce(
      (m, r) => Math.max(m, ...(r.volume_by_bucket || [0])),
      0
    );
    const dropped = d.segments.length - rows.length;
    const withCycles = rows.filter((r) => r.cycleRanges.length).length;
    return { rows, max, dropped, withCycles, buckets: d.timestamps || [] };
  }, [state.data]);

  if (!orgId) return null;

  const countNote = model
    ? `${model.rows.length} school${
        model.rows.length === 1 ? "" : "s"
      } with usage · ${model.withCycles} ran a detected push`
    : undefined;

  const has = !!(model && model.rows.length);

  const rowsGrid = has && (
    <div style={{ display: "grid", gap: ROW_GAP }}>
      {model.rows.map((r) => (
        <RibbonRow
          key={r.id}
          row={r}
          max={model.max}
          buckets={model.buckets}
          interval="week"
          cycles={r.cycleRanges}
          onHover={setHover}
        />
      ))}
    </div>
  );

  // One label strip for the pair. In the merged card it is drawn ABOVE the rows,
  // between the two plots, so it reads as the axis of either one — the mix's bars
  // end just above it and the schools' cells start just below. Standalone it sits
  // under the rows, where a lone plot's axis belongs.
  const axisStrip = has && showAxis && (
    <RibbonAxis
      buckets={model.buckets}
      interval="week"
      // ~8 labels across whatever the window holds; a year of weeks gets one
      // roughly every six, a half-year one every three.
      every={Math.max(1, Math.round((model.buckets.length - 1) / 8))}
      // above the rows in the merged card, so it needs its breathing room below
      style={bare ? { marginTop: 2, marginBottom: 8 } : undefined}
    />
  );

  const legendLine = has && (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        flexWrap: "wrap",
        fontSize: 11,
        color: MUTED,
      }}
    >
      <span>
        <span
          style={{
            display: "inline-block",
            width: 22,
            height: 8,
            verticalAlign: "middle",
            marginRight: 5,
            border: `1px solid ${CYCLE_STROKE}`,
            background: CYCLE_BAND_FILL,
            borderRadius: 2,
          }}
        />
        detected focus cycle · rightmost column is total lessons
        {model.dropped
          ? ` · ${model.dropped} school${
              model.dropped > 1 ? "s" : ""
            } with no lessons hidden`
          : ""}
      </span>
      {/* The hover read-out exists because a density cell has no other way to
          give up its number on a touch device, where `title` never fires. */}
      <span style={{ color: hover ? INK : MUTED }}>
        {hover
          ? `${hover.name} · ${hover.label}: ${hover.lessons.toLocaleString()} lessons`
          : "hover a week for its count"}
      </span>
    </div>
  );

  const notices = (
    <>
      {state.loading && (
        <div style={{ fontSize: 13, color: MUTED, padding: "8px 0" }}>
          <FA color="gray" icon="spinner" spin /> Finding each school's pushes…
        </div>
      )}
      {state.failed && (
        <div style={{ fontSize: 13, color: MUTED, padding: "8px 0" }}>
          Couldn’t load the adoption ribbon for this window.
        </div>
      )}
      {model && !model.rows.length && (
        <div style={{ fontSize: 13, color: MUTED, padding: "8px 0" }}>
          No school ran lessons in this window.
        </div>
      )}
    </>
  );

  if (bare)
    return (
      <>
        {notices}
        {axisStrip}
        {rowsGrid}
        {/* The caption sits UNDER its plot here, where the axis used to be: the
            axis had to move up between the two plots to serve both, and a panel
            title is the thing that reads fine either side of what it names. */}
        {has && (
          <div
            style={{
              marginTop: 10,
              paddingTop: 10,
              borderTop: `1px solid ${GRID}`,
            }}
          >
            <PanelHead
              style={{ margin: 0 }}
              title="Lessons per school"
              sub="One row per school, one cell per week — darker is more lessons."
              controls={
                countNote ? (
                  <span style={{ fontSize: 12, color: MUTED }}>{countNote}</span>
                ) : null
              }
            />
            <div style={{ marginTop: 6 }}>{legendLine}</div>
          </div>
        )}
      </>
    );

  return (
    <div style={{ margin: "0 0 24px" }}>
      <SectionHead title="Who pushes, and when" note={countNote} />
      {notices}
      {has && (
        <div
          style={{
            border: `1px solid ${CARD_BORDER}`,
            borderRadius: CARD_RADIUS,
            background: SURFACE,
            padding: "12px 14px",
          }}
        >
          {rowsGrid}
          {axisStrip}
          <div
            style={{
              marginTop: 8,
              paddingTop: 8,
              borderTop: `1px solid ${GRID}`,
            }}
          >
            {legendLine}
          </div>
        </div>
      )}
      {has && (
        <PlotNote
          style={{ margin: "10px 0 0" }}
          details={
            <>
              Cell darkness is scaled to the district's busiest school-week, so
              rows are comparable with each other rather than each normalized to
              itself. Outlined stretches are focus cycles detected per school
              from its own usage — schools rarely push at the same time, which is
              why a district-wide average can report “evenly spread” while every
              school in it ran a real push.
            </>
          }
        >
          One row per school, one cell per week; darker is more lessons.
        </PlotNote>
      )}
    </div>
  );
};

export { AdoptionRibbon, RIBBON_GUTTER_LEFT, RIBBON_GUTTER_RIGHT };
